import { useState } from 'react';

export default function TutoringForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    grade: '',
    subject: '',
    goals: '',
    availability: '',
    package: '',
    preferredDate: '',
    timeSlot: '',
    sessionFormat: ''
  });

  const [selectedCalendarSlot, setSelectedCalendarSlot] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const timeSlots = [
    { value: 'morning', label: 'Morning (9am–12pm)', icon: 'ri-sun-line' },
    { value: 'afternoon', label: 'Afternoon (12pm–4pm)', icon: 'ri-sun-cloudy-line' },
    { value: 'evening', label: 'Evening (4pm–8pm)', icon: 'ri-moon-line' }
  ];

  const sessionFormats = [
    { value: 'in-person', label: 'In-Person', icon: 'ri-user-location-line' },
    { value: 'online', label: 'Online via Zoom', icon: 'ri-vidicon-line' },
    { value: 'phone', label: 'Phone Consultation', icon: 'ri-phone-line' }
  ];

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const timeBands = ['Morning', 'Afternoon', 'Evening'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6nmn746fojmejgajv2g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          grade: '',
          subject: '',
          goals: '',
          availability: '',
          package: '',
          preferredDate: '',
          timeSlot: '',
          sessionFormat: ''
        });
        setSelectedCalendarSlot('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCalendarSlotClick = (day: string, time: string) => {
    const slot = `${day}-${time}`;
    setSelectedCalendarSlot(slot);
    setFormData({
      ...formData,
      availability: `${day} ${time}`
    });
  };

  const isFormComplete = formData.name && formData.email && formData.phone && 
                         formData.grade && formData.subject && formData.goals;

  return (
    <section id="tutoring-form" className="py-24 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
            Book Your Tutoring Session
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Schedule a personalized session with Mr. Augustine. Fill out the form below and select your preferred time.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-10 md:p-12">
          <form id="tutoring-request-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-base font-semibold text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base transition-colors duration-300"
                placeholder="Enter your full name"
              />
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-base font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base transition-colors duration-300"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-base font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base transition-colors duration-300"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Grade & Subject */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="grade" className="block text-base font-semibold text-gray-700 mb-2">
                  Grade Level / Test *
                </label>
                <select
                  id="grade"
                  name="grade"
                  value={formData.grade}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base transition-colors duration-300 cursor-pointer"
                >
                  <option value="">Select grade or test</option>
                  <option value="K-5">Elementary (K-5)</option>
                  <option value="6-8">Middle School (6-8)</option>
                  <option value="9-12">High School (9-12)</option>
                  <option value="SAT">SAT Prep</option>
                  <option value="ACT">ACT Prep</option>
                  <option value="GRE">GRE Prep</option>
                  <option value="GMAT">GMAT Prep</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="subject" className="block text-base font-semibold text-gray-700 mb-2">
                  Subject Area *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base transition-colors duration-300 cursor-pointer"
                >
                  <option value="">Select subject</option>
                  <option value="Algebra">Algebra</option>
                  <option value="Geometry">Geometry</option>
                  <option value="Trigonometry">Trigonometry</option>
                  <option value="Calculus">Calculus</option>
                  <option value="Statistics">Statistics</option>
                  <option value="General Math">General Math</option>
                  <option value="Test Prep">Test Prep</option>
                </select>
              </div>
            </div>

            {/* Package */}
            <div>
              <label htmlFor="package" className="block text-base font-semibold text-gray-700 mb-2">
                Preferred Package
              </label>
              <select
                id="package"
                name="package"
                value={formData.package}
                onChange={handleChange}
                className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base transition-colors duration-300 cursor-pointer"
              >
                <option value="">Select a package</option>
                <option value="Single Session">Single Session ($120/hour)</option>
                <option value="5 Sessions">5 Sessions Package (Save 10%)</option>
                <option value="10 Sessions">10 Sessions Package (Save 15%)</option>
                <option value="20 Sessions">20 Sessions Package (Save 20%)</option>
              </select>
            </div>

            {/* Goals */}
            <div>
              <label htmlFor="goals" className="block text-base font-semibold text-gray-700 mb-2">
                Learning Goals *
              </label>
              <textarea
                id="goals"
                name="goals"
                value={formData.goals}
                onChange={handleChange}
                required
                maxLength={500}
                rows={4}
                className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base transition-colors duration-300 resize-none"
                placeholder="What would you like to achieve? What topics do you need help with?"
              />
              <p className="text-sm text-gray-500 mt-2">Maximum 500 characters</p>
            </div>

            {/* Session Format */}
            <div>
              <label className="block text-base font-semibold text-gray-700 mb-3">
                Session Format
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {sessionFormats.map((format) => (
                  <button
                    key={format.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, sessionFormat: format.value })}
                    className={`p-4 border-2 rounded-lg transition-all duration-300 cursor-pointer ${
                      formData.sessionFormat === format.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-emerald-300'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <i className={`${format.icon} text-2xl ${
                        formData.sessionFormat === format.value ? 'text-emerald-600' : 'text-gray-600'
                      }`}></i>
                      <span className={`text-sm font-semibold ${
                        formData.sessionFormat === format.value ? 'text-emerald-700' : 'text-gray-700'
                      }`}>{format.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Preferred Date & Time Slot */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="preferredDate" className="block text-base font-semibold text-gray-700 mb-2">
                  Preferred Date
                </label>
                <input
                  type="date"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base transition-colors duration-300 cursor-pointer"
                />
              </div>
              <div>
                <label htmlFor="timeSlot" className="block text-base font-semibold text-gray-700 mb-2">
                  Preferred Time Slot
                </label>
                <select
                  id="timeSlot"
                  name="timeSlot"
                  value={formData.timeSlot}
                  onChange={handleChange}
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base transition-colors duration-300 cursor-pointer"
                >
                  <option value="">Select time slot</option>
                  {timeSlots.map((slot) => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Visual Calendar */}
            <div>
              <label className="block text-base font-semibold text-gray-700 mb-3">
                Available Weekly Time Slots
              </label>
              <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                <div className="grid grid-cols-7 gap-2">
                  {/* Header */}
                  <div className="text-center font-semibold text-sm text-gray-700"></div>
                  {weekDays.map((day) => (
                    <div key={day} className="text-center font-semibold text-sm text-gray-700 py-2">
                      {day}
                    </div>
                  ))}

                  {/* Time Slots */}
                  {timeBands.map((time) => (
                    <>
                      <div key={`label-${time}`} className="flex items-center justify-end pr-2 text-xs text-gray-600 font-medium">
                        {time}
                      </div>
                      {weekDays.map((day) => {
                        const slotKey = `${day}-${time}`;
                        const isSelected = selectedCalendarSlot === slotKey;
                        return (
                          <button
                            key={slotKey}
                            type="button"
                            onClick={() => handleCalendarSlotClick(day, time)}
                            className={`h-12 rounded-lg transition-all duration-300 cursor-pointer ${
                              isSelected
                                ? 'bg-emerald-500 text-white shadow-lg scale-105'
                                : 'bg-white hover:bg-emerald-100 border border-gray-200'
                            }`}
                          >
                            <span className="text-xs font-medium">
                              {isSelected ? '✓' : ''}
                            </span>
                          </button>
                        );
                      })}
                    </>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-4 text-center">
                  Click a slot to select your preferred availability
                </p>
              </div>
            </div>

            {/* Confirmation Summary */}
            {isFormComplete && (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border-2 border-emerald-200">
                <div className="flex items-center gap-3 mb-4">
                  <i className="ri-checkbox-circle-line text-2xl text-emerald-600"></i>
                  <h3 className="text-lg font-bold text-gray-900">Session Summary</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 mb-1">Student Name</p>
                    <p className="font-semibold text-gray-900">{formData.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Subject</p>
                    <p className="font-semibold text-gray-900">{formData.subject || 'Not selected'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Grade Level</p>
                    <p className="font-semibold text-gray-900">{formData.grade || 'Not selected'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Package</p>
                    <p className="font-semibold text-gray-900">{formData.package || 'Not selected'}</p>
                  </div>
                  {formData.preferredDate && (
                    <div>
                      <p className="text-gray-600 mb-1">Preferred Date</p>
                      <p className="font-semibold text-gray-900">{new Date(formData.preferredDate).toLocaleDateString()}</p>
                    </div>
                  )}
                  {formData.timeSlot && (
                    <div>
                      <p className="text-gray-600 mb-1">Time Slot</p>
                      <p className="font-semibold text-gray-900">
                        {timeSlots.find(s => s.value === formData.timeSlot)?.label || formData.timeSlot}
                      </p>
                    </div>
                  )}
                  {formData.sessionFormat && (
                    <div>
                      <p className="text-gray-600 mb-1">Session Format</p>
                      <p className="font-semibold text-gray-900">
                        {sessionFormats.find(f => f.value === formData.sessionFormat)?.label || formData.sessionFormat}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-lg font-semibold rounded-lg hover:opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Submitting...' : 'Confirm Booking Request'}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="p-5 bg-emerald-50 border-2 border-emerald-200 rounded-lg">
                <div className="flex items-center gap-3 text-emerald-700">
                  <i className="ri-checkbox-circle-line text-2xl"></i>
                  <p className="font-semibold">Thank you! We'll contact you within 24 hours to confirm your session.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-5 bg-red-50 border-2 border-red-200 rounded-lg">
                <div className="flex items-center gap-3 text-red-700">
                  <i className="ri-error-warning-line text-2xl"></i>
                  <p className="font-semibold">Something went wrong. Please try again or contact us directly.</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}